import type { Entry } from 'contentful'
import type { ButtonProps } from '@shc/ui'
import { routeMapper } from '@lib/mappers/menu-item-mapper'
import type {
  TypeHealthNewsTopicFields,
  TypeNewsArticleFields,
  TypePageFields,
  TypePersonFields,
} from '@lib/types'

export const ptMap = {
  '0px': 'pt-0', // none
  '20px': 'pt-5', // sm
  '40px': 'pt-5 lg:pt-10', // md
  '48px': 'pt-8 lg:pt-12', // lg
  '60px': 'pt-12 lg:pt-15', // xl
  '80px': 'pt-15 lg:pt-20', // 2xl
}

export const pbMap = {
  '0px': 'pb-0',
  '20px': 'pb-5',
  '40px': 'pb-5 lg:pb-10',
  '48px': 'pb-8 lg:pb-12',
  '60px': 'pb-12 lg:pb-15',
  '80px': 'pb-15 lg:pb-20',
}

export const ptAtLgMap = {
  '0px': 'lg:pt-0',
  '20px': 'lg:pt-5',
  '40px': 'lg:pt-10',
  '48px': 'lg:pt-12',
  '60px': 'lg:pt-15',
  '80px': 'lg:pt-20',
}

export const pbAtLgMap = {
  '0px': 'lg:pb-0',
  '20px': 'lg:pb-5',
  '40px': 'lg:pb-10',
  '48px': 'lg:pb-12',
  '60px': 'lg:pb-15',
  '80px': 'lg:pb-20',
}

export const cardWidthMap = {
  '5-up': 'w-full sm:w-grid-2 lg:w-grid-3 xl:w-grid-5',
  '4-up': 'w-full sm:w-grid-2 lg:w-grid-3 xl:w-grid-4',
  '3-up': 'w-full sm:w-grid-2 lg:w-grid-3',
  '2-up': 'w-full sm:w-grid-2',
}

// TODO: remove after captionedMedia SCL migration
export const aspectRatioMap = {
  '16:9': 16 / 9,
  '4:3': 4 / 3,
  '3:4': 3 / 4,
  '1:1': 1,
  original: undefined,
}

export const colWidthCenterMap = {
  '12 columns': 'col-span-12 col-start-1',
  '10 columns': 'col-span-12 col-start-1 lg:col-span-10 lg:col-start-2',
  '8 columns': 'col-span-12 col-start-1 lg:col-span-10 lg:col-start-2 xl:col-span-8 xl:col-start-3',
}

export const colWidthLeftMap = {
  '12 columns': 'col-span-12 col-start-1',
  '10 columns': 'col-span-12 col-start-1 lg:col-span-10 lg:col-start-1',
  '8 columns': 'col-span-12 col-start-1 lg:col-span-10 lg:col-start-1 xl:col-span-8 xl:col-start-1',
}

export const bgColorMap = {
  primary: 'bg-primary',
  'primary-50': 'bg-primary-50',
  secondary: 'bg-secondary',
  'secondary-50': 'bg-secondary-50',
  'gray-50': 'bg-gray-50',
  'campaign-secondary-800': 'bg-campaign-secondary-800',
  'campaign-gold-50': 'bg-campaign-gold-50',
  'campaign-gray-100': 'bg-campaign-gray-100',
  white: 'bg-white',
  transparent: 'bg-transparent',
}

export const bgImageMap = {
  cacti: 'bg-cacti',
  coastline: 'bg-coastline',
  floral: 'bg-floral',
  petals: 'bg-petals',
  succulent: 'bg-succulent',
  yucca: 'bg-yucca',
}

export const imageMap = {
  cacti: '/assets/backgrounds/cacti.webp',
  coastline: '/assets/backgrounds/coastline.webp',
  floral: '/assets/backgrounds/floral.webp',
  succulent: '/assets/backgrounds/succulent.webp',
  yucca: '/assets/backgrounds/yucca.webp',
}

export const campaignBgImageMap = {
  'white-starburst': 'bg-white-starburst',
  'gold-starburst': 'bg-gold-starburst',
  'gold-starburst-right': 'bg-gold-starburst-right',
}

export const columnsMaxCardMap: Record<'2' | '3' | '4' | '5', '2-up' | '3-up' | '4-up' | '5-up'> = {
  '2': '2-up',
  '3': '3-up',
  '4': '4-up',
  '5': '5-up',
}

export const campaignBackgroundColorMap = {
  'campaign-gray-100': 'bg-campaign-gray-100',
  'campaign-gold-50': 'bg-campaign-gold-50',
  white: 'bg-white',
}

export const containerWidthMap = {
  '8 columns': 8,
  '10 columns': 10,
  '12 columns': 12,
} as const

export const cardsPerRowMap = {
  '2': 2,
  '3': 3,
  '4': 4,
} as const

export const paddingMap = {
  '0px': 'none',
  '20px': 'sm',
  '40px': 'md',
  '48px': 'lg',
  '60px': 'xl',
  '80px': '2xl',
} as const

export const linkAttributes = (
  link?: Entry<
    TypeHealthNewsTopicFields | TypeNewsArticleFields | TypePageFields | TypePersonFields
  >,
  anchorOrExternalLink?: string
): ButtonProps | undefined => {
  const { route, isInternal } = routeMapper(link, anchorOrExternalLink)

  if (route === '') {
    return undefined
  }

  return {
    href: route,
    target: isInternal ? undefined : '_blank',
    rel: isInternal ? undefined : 'noopener noreferrer',
  }
}
